import {
    Component,
    defineElement,
    FC,
    useBindMethod,
    useElement,
    useElements,
    useEvent,
    useListen,
} from '@atomify/hooks';
import { useStylesheet } from '@utilities/hooks';

import { BPDInput } from './input';

export interface BPDFormElement extends Component {
    checkValidity: () => boolean;
    validate: (toggleErrorState?: boolean) => { valid: boolean };
    serializeForm: () => { [key: string]: any };
    clearInputs: () => void;
    formData: () => FormData | null;
    formInner: HTMLFormElement;
}

const BPDForm: FC<BPDFormElement> = ({ element }) => {
    useStylesheet('bpd-form { display: block }');

    const inputs = useElements<(BPDInput | null)[]>('bpd-input, bpd-radio, bpd-checkbox-list');
    const elements = useElements<(HTMLElement | null)[]>('select, input, textarea');
    const form = useElement<HTMLFormElement>('form', { as: 'formInner' });

    const serializedForm = useEvent({ eventName: 'serializedForm' });

    useBindMethod('checkValidity', checkValidity);
    useBindMethod('validate', validate);
    useBindMethod('serializeForm', serializeForm);
    useBindMethod('clearInputs', clearInputs);
    useBindMethod('formData', formData);

    useListen(form, 'submit', (e) => {
        if (e) e.preventDefault();

        const { valid } = validate();

        if (valid) {
            serializeForm();
        }
    });

    function formData() {
        if (form.current) {
            return new FormData(form.current);
        }

        return null;
    }

    function checkValidity() {
        if (!inputs.current) return false;

        return [...inputs.current]?.filter((input: BPDInput) => !input || !input.valid).length >= 1;
    }

    function validate(toggleErrorState: boolean = true): { valid: boolean } {
        if (!form) return { valid: false };

        if (form.current?.getAttribute('novalidate')) return { valid: true };

        let valid = form.current?.checkValidity() || false;

        Array.from(inputs.current).forEach((input) => {
            if (input && input.validate) {
                valid = !!input.validate(toggleErrorState) && valid;
            }
        });

        return {
            valid,
        };
    }

    function serializeForm() {
        const data: { [key: string]: any } = {};
        const listItems: { [key: string]: Array<any> } = {};

        const extendedSelectInput = element.querySelector<HTMLSelectElement>('select');
        const extendElements = [...elements.current, extendedSelectInput].filter(Boolean);

        extendElements.forEach((element: HTMLInputElement) => {
            const isRadioButton = element?.type === 'radio';
            const isEmptyCheckboxOrRadio =
                (isRadioButton || element?.type === 'checkbox') && !element.checked;

            const elementName = element.name;
            const isCheckboxList = elementName.includes('[]');
            const inputValue = isEmptyCheckboxOrRadio ? '' : element.value;

            if (isRadioButton) {
                // Check if radio button is already there (because radio has two inputs) and the incoming radio has a value update it.
                // Or update the data when there is no previous radio available.
                if ((data[elementName] && inputValue) || !data[elementName]) {
                    data[elementName] = inputValue;
                }
                return;
            } else if (!isCheckboxList) {
                data[elementName] = inputValue;
                return;
            } else {
                const inputName = elementName.replace('[]', '');
                if (!listItems[inputName]) listItems[inputName] = [];
                if (inputValue) listItems[inputName].push(inputValue);
                const values = listItems[inputName];
                data[inputName] = values.join(values.length > 1 ? ',' : '');
                return;
            }
        });

        serializedForm.emit(data);

        return data;
    }

    function clearInputs() {
        if (!inputs.current) return;

        Array.from(inputs.current).forEach((input) => {
            if (input && input.resetInput) {
                input.resetInput();
            }
        });
    }
};

defineElement('bpd-form', BPDForm);
